// Floating "+" navbar. Appears on the workspace page and on canvas
// project pages so "create" is one click away regardless of route.
// Canvas route is `/:owner/:project`; workspace route is `/workspaces/:slug`.
//
// Picker overlays (IntegrationPickerOverlay / LibraryPickerOverlay /
// NewProjectPickerOverlay) are mounted in CanvasEditor so they appear on
// BOTH /workspaces/<slug> AND /<owner>/<key>. Setting root state is all
// that's needed — no redirects.
const openPicker = (el, s, state) => {
  s.root?.update({ createMenuOpen: false, ...state })
}

// Open the template-picker modal.
// `opts.templateId` pre-selects a template card (default: 'blank').
// `opts.projectType` is 'library' when "New library" is clicked — we pre-set
// the templateId to 'library' so the card starts highlighted.
const openNewProjectPicker = (el, s, opts) => {
  s.root?.update({
    createMenuOpen: false,
    newProjectPicker: {
      open: true,
      tab: 'featured',
      templateId: (opts && opts.templateId) || 'blank',
      templateProject: (opts && opts.templateProject) || null,
      projectType: (opts && opts.projectType) || null,
      name: '',
      creating: false,
      error: null,
      _libList: null,  // populated lazily when library tab renders
      _libQuery: ''    // search query for library tab picker
    }
  })
}

// orgIdFromCaches is no longer called from WorkspaceNavbar directly
// (moved into NewProjectPickerOverlay's submitNewProjectPicker in workspace.js),
// but keep the helper here for any downstream callers that might reference it.

const menuItem = (label, hint, iconName, onClick) => ({
  tag: 'button',
  type: 'button',
  attr: { role: 'menuitem' },
  title: label,
  display: 'flex',
  flow: 'y',
  gap: '2px',
  padding: 'Y Z',
  round: 'Z',
  border: '0',
  background: 'transparent',
  color: 'white',
  textAlign: 'left',
  cursor: 'pointer',
  ':hover': { background: 'gray6.05' },
  Name: {
    tag: 'span',
    display: 'flex',
    flow: 'x',
    gap: 'Y',
    align: 'center left',
    Icon: {
      icon: iconName,
      fontSize: 'Z2',
      opacity: '0.65',
      ariaHidden: 'true'
    },
    Label: {
      tag: 'span',
      text: label,
      fontSize: 'Z2',
      fontWeight: '600'
    }
  },
  Hint: { tag: 'span', text: hint, fontSize: 'Z', color: 'white.55' },
  onClick
})

export const LabsNavbar = {
  position: 'absolute',
  top: '50%',
  left: 'V',
  transform: 'translateY(-50%)',
  zIndex: 30,
  padding: 'V',
  round: 'C1',
  theme: 'canvas-card-glass',
  border: '1px solid rgba(255,255,255,0.08)',
  flow: 'y',
  gap: 'V',
  align: 'center',
  pointerEvents: 'auto',
  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',

  AddBtn: {
    tag: 'button',
    type: 'button',
    width: '36px',
    height: '36px',
    round: '50%',
    border: '0',
    background: 'rgba(255,255,255,0.08)',
    color: 'white',
    fontSize: 'B',
    fontWeight: '300',
    lineHeight: '1',
    cursor: 'pointer',
    title: 'Create',
    ariaLabel: 'Create project or library',
    ariaHaspopup: 'true',
    ariaExpanded: (el, s) => s.root?.createMenuOpen ? 'true' : 'false',
    text: '+',
    ':hover': { background: 'rgba(255,255,255,0.18)' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.root?.update({ createMenuOpen: !s.root?.createMenuOpen })
    }
  },

  CreateMenu: {
    display: (el, s) => (s.root?.createMenuOpen ? 'flex' : 'none'),
    position: 'absolute',
    top: '50%',
    left: '100%',
    marginLeft: 'V',
    transform: 'translateY(-50%)',
    minWidth: '220px',
    maxHeight: 'min(60vh, calc(100vh - Z1))',
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: 'V',
    round: 'Z1',
    theme: 'canvas-card-glass',
    shape: 'tooltip',
    shapeDirection: 'left',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
    flow: 'y',
    gap: '2px',
    role: 'menu',
    ariaLabel: 'Create new…',

    onKeyDown: (e, el, s) => {
      const items = el.node.querySelectorAll('[role="menuitem"]')
      const idx = Array.from(items).indexOf(document.activeElement)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const next = items[(idx + 1) % items.length]
        next && next.focus()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prev = items[(idx - 1 + items.length) % items.length]
        prev && prev.focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        items[0] && items[0].focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        items[items.length - 1] && items[items.length - 1].focus()
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        s.root?.update({ createMenuOpen: false })
      }
    },

    // --- Section 1: Project type quick-creates ---
    NewApp: menuItem(
      'New app',
      'Blank web app project.',
      'globe',
      (e, el, s) => openNewProjectPicker(el, s, { templateId: 'blank', projectType: 'app' })
    ),
    NewPresentation: menuItem(
      'New Presentation',
      'Slide deck (Symbols deck template).',
      'copy',
      (e, el, s) => openNewProjectPicker(el, s, { templateId: 'deck', projectType: 'deck', templateProject: 'commercials/deck' })
    ),
    NewMobile: menuItem(
      'New Mobile app',
      'Mobile-first project.',
      'deviceMobile',
      (e, el, s) => openNewProjectPicker(el, s, { templateId: 'blank', projectType: 'mobile' })
    ),
    NewDesktop: menuItem(
      'New Desktop app',
      'Desktop application project.',
      'deviceSmallScreen',
      (e, el, s) => openNewProjectPicker(el, s, { templateId: 'blank', projectType: 'desktop' })
    ),
    NewTV: menuItem(
      'New TV app',
      'TV / large-screen project.',
      'deviceBigScreen',
      (e, el, s) => openNewProjectPicker(el, s, { templateId: 'blank', projectType: 'tv' })
    ),
    NewLibrary: menuItem(
      'New Library',
      'Reusable component library.',
      'library',
      (e, el, s) => openNewProjectPicker(el, s, { templateId: 'library', projectType: 'library' })
    ),

    // --- Divider between creates and utilities ---
    Divider: {
      extends: 'Line',
      role: 'separator',
      margin: 'V 0'
    },

    // --- Section 2: Utilities ---
    LinkLibrary: menuItem(
      'Link library',
      'Import an existing project as a shared library.',
      'globeOutline',
      (e, el, s) => openPicker(el, s, { libraryPickerFor: '__standalone__', libraryPickerQuery: '' })
    ),
    NewIntegration: menuItem(
      'New integration',
      'Connect a service (Supabase, GitHub, …).',
      'plus',
      (e, el, s) => openPicker(el, s, {
        integrationPicker: {
          open: true,
          step: 'kind',
          kinds: s.root?.integrationPicker?.kinds || null,
          query: '',
          selectedKind: null,
          form: {}
        }
      })
    ),
    NewMcpConnector: menuItem(
      'New MCP connector',
      'Add an MCP server (tools, resources).',
      'terminal',
      (e, el, s) => s.root?.update({
        createMenuOpen: false,
        _mcpConnectorPickerOpen: true,
        _mcpConnectorDraft: {
          transport: 'sse',
          authKind: 'none',
          scopeType: 'workspace',
          scopeId: null
        }
      })
    )
  }
}
